import { authFetch } from '../utils/authFetch';
import { APArchetype, APArchetypesResponse } from '../types/apArchetypes';

const API_BASE = (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1';

export const apArchetypesService = {
  // Get all AP archetypes
  async getAPArchetypes(
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<APArchetype[]> {
    const response = await authFetch(`${API_BASE}/ap-archetypes`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch AP archetypes');
    }
    const data: APArchetypesResponse = await response.json();
    return data.data;
  },

  // Get AP archetype by ID
  async getAPArchetype(
    id: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<APArchetype> {
    const response = await authFetch(`${API_BASE}/ap-archetypes/${id}`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch AP archetype');
    }
    const data = await response.json();
    return data;
  },

  // Get AP archetype by name
  async getAPArchetypeByName(
    name: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<APArchetype> {
    const response = await authFetch(`${API_BASE}/ap-archetypes/name/${encodeURIComponent(name)}`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch AP archetype by name');
    }
    const data = await response.json();
    return data;
  },

  // Create AP archetype with optional entries
  async createAPArchetype(
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void,
    body: {
      name: string;
      description?: string;
      is_default?: boolean;
      entries?: Array<{ key: string; value: any }>;
    }
  ): Promise<APArchetype> {
    const response = await authFetch(`${API_BASE}/ap-archetypes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }, accessToken, setUserAndToken);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create AP archetype');
    }
    const data = await response.json();
    return data.data;
  },

  // Update basics and order_to_evaluate
  async updateBasics(
    id: string,
    body: { name?: string; description?: string; is_default?: boolean; order_to_evaluate?: number },
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<APArchetype> {
    const response = await authFetch(`${API_BASE}/ap-archetypes/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }, accessToken, setUserAndToken);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to update archetype');
    }
    const data = await response.json();
    return data.data;
  },

  // Create or update descriptions (batch)
  async upsertDescriptions(
    id: string,
    entries: Array<{ id?: string; key: string; value: any }>,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<APArchetype> {
    const response = await authFetch(`${API_BASE}/ap-archetypes/${id}/descriptions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries })
    }, accessToken, setUserAndToken);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to upsert descriptions');
    }
    const data = await response.json();
    return data.data;
  },

  // Delete a description by key (prefix not supported here; exact key)
  async deleteDescription(
    id: string,
    key: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<APArchetype> {
    const response = await authFetch(`${API_BASE}/ap-archetypes/${id}/descriptions/${encodeURIComponent(key)}`, {
      method: 'DELETE'
    }, accessToken, setUserAndToken);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to delete description');
    }
    const data = await response.json();
    return data.data;
  },
};
